import {startCase} from "lodash"
import React, {useMemo} from "react"
import {useQueryState} from "use-location-state"
import {useToasts} from "react-toast-notifications"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {usePaging} from "../../hooks/usePaging"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {BillStatus, exportBills, findBills} from "../../resources/bills"
import {useAccessToken, useUserType} from "../../services/auth"
import {downloadFile} from "../../utilities/file"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {Filter} from "../Filter/Filter"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs, Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {ErrorDocument} from "../../resources/common"
import {BillsTable} from "./BillsTable"

export enum BillsColumns {
	id = "Id",
	org = "Org",
	invoiceNumber = "Invoice #",
	customer = "Customer",
	vendor = "Vendor",
	amount = "Amount",
	dueDate = "Due Date",
	deductionDate = "Deduction Date",
	date = "Date",
	status = "Status",
	createdAt = "Created At",
}

export type AllowedBillsColumns =
	| BillsColumns.id
	| BillsColumns.org
	| BillsColumns.invoiceNumber
	| BillsColumns.customer
	| BillsColumns.vendor
	| BillsColumns.amount
	| BillsColumns.dueDate
	| BillsColumns.deductionDate
	| BillsColumns.date
	| BillsColumns.status
	| BillsColumns.createdAt

interface BillsProps {
	limit?: number
	token?: string
	includedColumns: AllowedBillsColumns[]
	enableTitle?: boolean
	fullHeight?: boolean
	enableOrgFilter?: boolean
	enableStatusFilter?: boolean
	enablePaging?: boolean
	enableDateFilter?: boolean
	title?: string
	customerId?: string
	vendorId?: string
}

export default function Bills({
	limit = 25,
	token,
	includedColumns,
	fullHeight,
	enableTitle,
	enableOrgFilter = false,
	enableStatusFilter = false,
	enablePaging = true,
	enableDateFilter = true,
	title = "Bills",
	customerId,
	vendorId,
}: BillsProps) {
	const prefix = "bills"
	const accessToken = token ?? useAccessToken()
	const {addToast} = useToasts()
	const [refreshToken] = useRefreshToken()
	const userType = useUserType()
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const statusesOptions = new Map<BillStatus, string>(Object.values(BillStatus).map((v) => [v, startCase(v)]))
	const defaultStatuses = Object.values(BillStatus).filter(
		(s) => s !== BillStatus.Draft && s !== BillStatus.Archived
	) as Array<BillStatus>
	const [statuses, setStatuses] = useQueryState<Array<BillStatus>>(`${prefix}-filter[status]`, defaultStatuses)
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findBills(
				accessToken,
				offset,
				sortBy,
				"",
				limit,
				statuses,
				filteredOrgs,
				since,
				until,
				customerId,
				vendorId ? [vendorId] : undefined
			),
		(x) => x.data.length,
		[refreshToken, since, until, sortBy, [...statuses, ...filteredOrgs].join(","), customerId ?? "", vendorId ?? ""],
		`${prefix}-${customerId ?? ""}-${vendorId ?? ""}`,
		true
	)

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	function toggleSort() {
		setSortBy(sortBy === "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	async function exportBillsFunc() {
		const response = await exportBills(
			accessToken,
			statuses,
			sortBy,
			filteredOrgs,
			since,
			until,
			customerId,
			vendorId ? [vendorId] : undefined
		)

		response.match(
			(d) => downloadFile("Bills.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)

		return true
	}

	return (
		<DataTableCard className="payments-card">
			<DataTableActionHeader
				enableSticky={fullHeight}
				dateTimePicker={enableDateFilter ? dateTime : null}
				filters={[...(enableOrgFilter ? [orgFilter] : []), ...(enableStatusFilter ? [statusFilter] : [])]}
				title={enableTitle ? title : null}
				exportFunc={exportBillsFunc}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: bills}) => (
					<BillsTable
						bills={bills.data}
						meta={bills.meta}
						hasResults={hasResults}
						hasPrev={hasPrev}
						hasNext={hasNext}
						prev={prev}
						next={next}
						isUsingPaging={enablePaging}
						include={bills.included}
						fullHeight={fullHeight}
						includedColumns={includedColumns}
						sortFunction={toggleSort}
						sortBy={sortBy}
					/>
				)}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
